import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FormFileUploadComponent, FormInputComponent, FormNumberComponent, FormWysiwygComponent } from '@shared/forms';
import { MediaFacade } from '@app/store/media';
import { MediaType, getMediaTypeAccept } from '@app/store/shared/models';
import { MediaFormService } from '../../media-form.service';

@Component({
    selector: 'app-add-form',
    templateUrl: './add-form.component.html',
    styleUrls: ['./add-form.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        RouterLink,
        FormInputComponent,
        FormFileUploadComponent,
        FormNumberComponent,
        FormWysiwygComponent,
    ],
    providers: [MediaFormService],
})
export class AddFormComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly mediaFacade = inject(MediaFacade);
    private readonly mediaFormService = inject(MediaFormService);
    private readonly translateService = inject(TranslateService);
    readonly type = <MediaType>(this.route.snapshot.params['type'] ?? 'image');
    readonly saving = this.mediaFacade.saving;
    readonly form = this.mediaFormService.form;
    readonly fileAccept = computed(() => getMediaTypeAccept(this.type));

    readonly title = computed(() => {
        const typeLabel = this.translateService.instant(`media.type.${this.type}`);
        return this.translateService.instant('media.modal.add.typeTitle', { type: typeLabel });
    });

    constructor() {
        this.form.controls.type.setValue(this.type);

        effect(() => {
            if (this.mediaFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.mediaFacade.saving() === false && this.mediaFacade.saved() === false) {
                this.form.enable();
                this.mediaFormService.onTypeChange(this.type);
            }
        });
    }

    ngOnDestroy(): void {
        this.mediaFacade.clear();
    }

    onSubmit(): void {
        const value = this.form.getRawValue();
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        this.form.disable();
        this.mediaFacade.createMedia({
            name: value.name,
            type: this.type,
            mediaId: value.mediaId || undefined,
            content: value.content ?? undefined,
            duration: value.duration ?? undefined,
        });
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
